import { observable, action } from 'mobx';
import { toast } from 'react-toastify';
import each from 'lodash/each';
import { fetchPlanning } from '../api/planning';
import { addEmployeeToShift, removeEmployeeFromShift } from '../api/shift';

import pubSub from '../utils/pubSub';

let fetchingDays = [];

export default class PlanStore {
  @observable days = {}

  @observable shiftModalIsOpen = false

  @observable newItemModalIsOpen = false

  @observable selectedDay = null

  @observable addItemDate = null

  @observable selectedShiftId = null

  @observable lastUpdated = new Date().getTime()

  @observable skipToColumn = null

  @action
  fetchPlanning(date) {
    if (
      fetchingDays.indexOf(date) >= 0 ||
      typeof this.days[date] !== 'undefined'
    ) {
      return Promise.resolve();
    }

    // Prevent refetching on multiple fetchPlanning calls
    fetchingDays.push(date);

    return fetchPlanning(date)
      .then(this.fetchPlanningSuccess)
      .catch(this.fetchPlanningError);
  }

  @action.bound
  fetchPlanningSuccess(res) {
    const { data } = res;

    // Remove the date from the fetching array
    fetchingDays.splice(fetchingDays.indexOf(data.date), 1);

    // Create object to insert back into the store dates
    const day = {};
    day[data.date] = Object.assign({}, data);

    this.days = Object.assign(this.days, day);

    return res;
  }

  @action.bound
  fetchPlanningError() {
    toast.error('Error bij het ophalen van de planning');
  }

  /**
   * Fetch the planning information for this day. If in cache
   * it will be returned immediately. If not, it will return
   * an object with no values.
   *
   * @param  {Date} dateFormatted
   * @return {Promise}
   */
  @action
  getPlanningForDay(dateFormatted) {
    return new Promise((resolve) => {
      if (
        typeof this.days === 'undefined' ||
        typeof this.days[dateFormatted] === 'undefined'
      ) {
        return this.fetchPlanning(dateFormatted)
          .then((res) => {
            resolve(res.data);
          })
          .catch((err) => {
            console.error(err, dateFormatted);
          });
      }

      resolve(this.days[dateFormatted]);
    });
  }

  @action
  getPlanningForDaySync(dateFormatted) {
     if (typeof this.days[dateFormatted] === 'undefined') {
       this.fetchPlanning(dateFormatted);
       return null;
     }

     return this.days[dateFormatted];
  }

  @action
  refreshPlanningForDay(dateFormatted) {
    if (typeof this.days[dateFormatted] !== 'undefined') {
      delete this.days[dateFormatted];
      return this.getPlanningForDay(dateFormatted);
    }

    return Promise.reject();
  }

  @action
  setSelectedDay(val) {
    this.selectedDay = val;
    this.lastUpdated = new Date().getTime();
  }

  @action
  setSelectedShiftId(val) {
    this.selectedShiftId = val;
    this.lastUpdated = new Date().getTime();
  }

  @action
  setSkipToColumn(index) {
    this.skipToColumn = index;
  }

  @action
  toggleModal(force, key = 'shiftModalIsOpen') {
    if (force === false) {
      pubSub.publish('fetch:employees');
    }

    if (force) {
      this[key] = force;
      return;
    }

    this[key] = !this[key];
  }

  @action
  setAddItemDate(date) {
    this.addItemDate = date;
  }

  @action
  addEmployee(employeeId, shiftId) {
    if (typeof this.days === 'undefined') {
      return;
    }

    return addEmployeeToShift(employeeId, shiftId)
      .then(this.addEmployeeSuccess)
      .then((res) => {
        this.refreshDayOfChangedShift(shiftId);
        return res;
      })
      .catch(this.addEmployeeError);
  }

  @action
  removeEmployee(employeeId, shiftId) {
    if (typeof this.days === 'undefined') {
      return;
    }

    return removeEmployeeFromShift(employeeId, shiftId)
      .then(this.removeEmployeeSuccess)
      .then((res) => {
        this.refreshDayOfChangedShift(shiftId);
        return res;
      })
      .catch(this.removeEmployeeError);
  }

  @action.bound
  addEmployeeSuccess() {
    toast.success('Medewerker toegevoegd aan ploeg');
  }

  @action.bound
  addEmployeeError(err) {
    toast.error('Fout bij het toevoegen van een medewerker aan de ploeg');
  }

  @action.bound
  removeEmployeeSuccess() {
    toast.success('Medewerker verwijderd van ploeg');
  }

  @action.bound
  removeEmployeeError(err) {
    toast.error('Fout bij het verwijderen van een medewerker van een ploeg');
  }

  @action
  refreshDayOfChangedShift(shiftId) {
    const refreshDays = [];

    each(this.days, (day) => {
      if (typeof day.shifts === 'undefined' || !day.shifts.length) {
        return;
      }

      day.shifts.forEach((shift) => {
        if (shift.id === shiftId) {
          refreshDays.push(day.date);
        }
      });
    });

    refreshDays.forEach((day) => {
      pubSub.publish('fetch:day', day);
    });
  }
}



// WEBPACK FOOTER //
// ./src/store/planStore.js